import { CellValueChangedEvent } from 'ag-grid-community';
import React, { useCallback, useEffect, useMemo } from 'react';
import 'styles/ag-grid.css';
import { useTranslation } from 'react-i18next';
import { SubmissionTabProps } from 'routes/Submissions/SubmissionDetail/navigation-tab/submission-config-tabs';
import FormActionButtons from 'components/ui/form-action-buttons';
import { EQueryKeys } from 'constants/query-keys';
import { basisOfCoverApi } from 'services/apis';
import { useMutation, useQuery } from 'react-query';
import {
  BasisOfCoverResponseDtoCategoryEnum,
  BasisOfCoverResponseDtoBasisOfCoverEnum,
  UpsertBasisOfCoverPayloadDto,
} from 'libs/pinnacle-sdk';
import { handleError } from 'utils/form';
import ExcelTable from 'components/ui/excel-table';
import {
  EExcelTableColumnType,
  ExcelTableColDef,
} from 'components/ui/excel-table/types';
import { isSubmissionEditable } from 'utils/submission';

const DEFAULT_BASIS_OF_COVERS: UpsertBasisOfCoverPayloadDto[] = [
  {
    category: BasisOfCoverResponseDtoCategoryEnum.Fire,
    basisOfCover: BasisOfCoverResponseDtoBasisOfCoverEnum.AnyOneLocation,
  },
  {
    category: BasisOfCoverResponseDtoCategoryEnum.Flood,
    basisOfCover: BasisOfCoverResponseDtoBasisOfCoverEnum.AnyOneEvent,
  },
  {
    category: BasisOfCoverResponseDtoCategoryEnum.Wind,
    basisOfCover: BasisOfCoverResponseDtoBasisOfCoverEnum.AnyOneEvent,
  },
  {
    category: BasisOfCoverResponseDtoCategoryEnum.Earthquake,
    basisOfCover: BasisOfCoverResponseDtoBasisOfCoverEnum.AnyOneEvent,
  },
];

const BasisOfCoverTable: React.FC<SubmissionTabProps> = ({
  submission,
  includeBack,
  includeNext,
  navigateBack,
  navigateNext,
}) => {
  const { t } = useTranslation();

  const isEditingMode = useMemo(() => {
    return isSubmissionEditable(submission.status);
  }, [submission.status]);

  const {
    data: basisOfCovers,
    isLoading,
    refetch,
  } = useQuery(
    [EQueryKeys.BASIS_OF_COVERS, submission.submissionId],
    () =>
      basisOfCoverApi.getBasisOfCovers({
        submissionId: submission.submissionId,
      }),
    {
      enabled: !!submission.submissionId,
    }
  );

  const upsertBasisOfCoversMutation = useMutation(
    async (rowData: UpsertBasisOfCoverPayloadDto[]) => {
      await basisOfCoverApi.upsertBasisOfCovers({
        submissionId: submission.submissionId,
        upsertBasisOfCoverPayloadDto: rowData,
      });
    },
    {
      onError: async (error: { response: Response }) => {
        handleError({
          error,
          defaultMessage: t('basisOfCover.recordUpdatedFailed'),
        });
      },
    }
  );

  const columnDefs = useMemo<ExcelTableColDef[]>(
    () => [
      {
        field: 'category',
        headerName: t('basisOfCover.headers.category'),
        flex: 1,
        minWidth: 200,
        editable: false,
      },
      {
        field: 'basisOfCover',
        headerName: t('basisOfCover.headers.basisOfCover'),
        flex: 2,
        minWidth: 250,
        columnType: EExcelTableColumnType.SELECTION,
        selectionOptions: Object.values(
          BasisOfCoverResponseDtoBasisOfCoverEnum
        ).map((value) => ({
          value,
          label: value,
        })),
        editable: isEditingMode,
      },
    ],
    [t, isEditingMode]
  );

  const rowData = useMemo(
    () =>
      basisOfCovers?.length
        ? basisOfCovers.map((item) => ({
            category: item.category,
            basisOfCover: item.basisOfCover,
          }))
        : DEFAULT_BASIS_OF_COVERS,
    [basisOfCovers]
  );

  useEffect(() => {
    if (basisOfCovers && basisOfCovers.length === 0) {
      upsertBasisOfCoversMutation
        .mutateAsync(DEFAULT_BASIS_OF_COVERS)
        .then(() => {
          refetch();
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basisOfCovers]);

  const onCellValueChanged = useCallback(
    async (params: CellValueChangedEvent) => {
      const changedRow: UpsertBasisOfCoverPayloadDto = {
        category: params.data.category,
        basisOfCover: params.data.basisOfCover,
      };
      await upsertBasisOfCoversMutation.mutateAsync([changedRow]);
    },
    [upsertBasisOfCoversMutation]
  );

  return (
    <div className="basis-of-cover-table">
      <ExcelTable
        onCellValueChanged={isEditingMode ? onCellValueChanged : undefined}
        tableContainerClassName="h-[17rem] mb-2"
        loading={isLoading}
        columnDefs={columnDefs}
        data={rowData}
      />

      <FormActionButtons
        confirmText="Next"
        cancelText="Back"
        isConfirmDisabled={upsertBasisOfCoversMutation.isLoading}
        includeCancel={includeBack}
        includeConfirm={includeNext}
        confirmOutlined={false}
        onCancel={navigateBack}
        onConfirm={navigateNext}
      />
    </div>
  );
};

export default BasisOfCoverTable;
